import { apiBaseUrl } from '../api/baseUrl';
import { beginScopeTransition } from '../api/scopeTransition';
import { noteIfGateway, noteRestoreUnreachable } from './restoreReachability';
import { createInertSessionChannel, type SessionChannel } from './sessionChannel';

// ─────────────────────────────────────────────────────────────────────
// THE CLUB PORTAL SESSION (#186).
//
// Two authentication systems already exist on the API and neither was reachable
// from the portal:
//
//   Entra        Starpoint's own staff, for the /sa control panel (staffAuth).
//   club session `sps_` tokens from POST /auth/club/login, validated against
//                our own database — the accounts club administrators actually
//                have.
//
// The portal's own login was the stage-1 SHARED DEMO CREDENTIAL, which proves
// nothing to the API and carries no authority: with VITE_API_URL set, the app
// signed in happily and then every request answered 401. This module is the
// missing half.
//
// THE TOKEN LIVES IN MEMORY, AND THE SESSION SURVIVES ANYWAY (#569).
//
// Not localStorage, not sessionStorage — still. A bearer token in web storage is
// readable by any script that gets into the page, and it outlives the tab that
// earned it. That reasoning has not changed and neither has this module: the
// token is held in memory and dies with the page.
//
// WHAT CHANGED IS THAT «SIGNED IN» NO LONGER DEPENDS ON IT. This comment used
// to end «a reload returns to the login screen, and that is the intended
// behaviour rather than a gap to paper over with a stored copy». Being signed
// out by a refresh, and unable to use a second tab, turned out to be a cost
// nobody had agreed to pay — and `localStorage` was never the only alternative.
// The API now also issues the SAME session as a `HttpOnly; Secure;
// SameSite=Lax` cookie, which this code cannot read and does not try to:
// `restore()` asks `/me` on boot and the server answers who it is.
//
// So there are two shapes of a live session here, and the difference matters:
//
//   just signed in    `token` is set. The transport sends it as a bearer
//                     header, which the server prefers whenever it is present.
//   after a reload    `token` is null and the cookie authenticates. Every
//                     request still carries `credentials: 'include'`, and the
//                     server falls back to the cookie only when no header came.
//
// Nothing here may reintroduce a readable copy of the token to collapse those
// two into one. See `apps/api/src/auth/sessionCookie.ts` for the other half.
//
// It is also never logged and never returned by anything but `token()`, which
// only the transport calls.
// ─────────────────────────────────────────────────────────────────────

/** The account behind a session, as the API describes it. */
export interface ClubAccount {
  id: string;
  email: string | null;
  displayName: string | null;
}

export type ClubSessionStatus = 'signed-out' | 'signed-in';

// ── SCOPE (#186) ─────────────────────────────────────────────────────
//
// «Where may I act?», kept separate from «who are you?». Signing in identifies
// the PERSON; selecting a scope happens after, and an account can be perfectly
// well authenticated with no scope to work in.
//
// ORGANIZATION-NEUTRAL naming, deliberately: the portal calls it a club and the
// backend stores a `Tenant`, but centre-versus-club is not decided (#302). A
// type called `Club` would have to be renamed the day it is; `scope` does not.

/** Somewhere the signed-in account may operate. */
export interface Scope {
  /** The backend's tenant id — what travels in X-Starpoint-Tenant. */
  tenantId: string;
  slug: string;
  name: string;
  /** `CENTRE` or `CLUB` — what kind of organization this is. */
  kind: string;
  /**
   * Keys of the roles held HERE. For rendering only; the server re-decides
   * every call. A list, because roles are data and one person may hold several.
   */
  roles: string[];
}

export type ScopeStatus =
  /** Not signed in, or not asked yet. */
  | 'none'
  /** Signed in, and exactly one scope was available — selected automatically. */
  | 'selected'
  /** Signed in, several available, waiting for the person to choose. */
  | 'choosing'
  /**
   * Signed in, and nowhere to act. The server ANSWERED, and the answer was
   * that this account holds no membership. Authenticated is not the same as
   * entitled, and only somebody else can change it.
   */
  | 'unavailable'
  /**
   * Signed in, and we do not KNOW where they may act — the question failed.
   *
   * Deliberately distinct from `unavailable`. «You have no access» and «we
   * could not find out» are different facts with different remedies: the first
   * needs an administrator, the second needs a retry. Collapsing them tells
   * someone whose network blipped to go and ask for permission they already
   * have, and it hides an outage behind a message that sounds like policy.
   */
  | 'undetermined';

export interface ClubSessionState {
  status: ClubSessionStatus;
  account: ClubAccount | null;
  /** Everything this account may act in. Empty until sign-in resolves it. */
  scopes: Scope[];
  scopeStatus: ScopeStatus;
  /** The scope requests are made in, or null. */
  currentScope: Scope | null;
  /**
   * Why the session ended, when it ended on its own.
   *
   * `'unauthorized'` means the API rejected the token — expired, revoked, or
   * the account disabled. The gate shows that rather than a bare login screen,
   * because "you were signed out" and "you never signed in" are different
   * things to a person who was halfway through something.
   */
  endedBecause: 'unauthorized' | 'signed-out' | null;
}

export interface ClubSignInResult {
  success: boolean;
  error?: string;
  /**
   * WHY it failed, as a CODE, where the answer is more than «those credentials
   * did not work» (#698).
   *
   * A code and not a sentence, for the reason `LoginScreen`'s `notice` and the
   * passkey failures are codes: this module has no catalog and must not have
   * one, and the screen decides what is said and in which language.
   *
   * Only `account_disabled` so far. The generic refusal keeps carrying `error`,
   * because migrating the whole sign-in path to codes is a bigger change than
   * the one defect being fixed — and a half-migrated path with two mechanisms
   * is worse than one honest one plus a named exception.
   */
  code?: 'account_disabled';
}

export interface ClubSession {
  state(): ClubSessionState;
  /** The bearer token, or null when there is no live session. */
  token(): string | null;
  /**
   * The tenant id every request should be scoped to, or null.
   *
   * Null while choosing or when nothing is available — and the transport then
   * sends no tenant header at all, so the server answers `tenant_required`
   * rather than being handed a guess.
   */
  tenantId(): string | null;
  /** Choose which scope to act in. Ignores an id that is not on offer. */
  selectScope(tenantId: string): void;
  /**
   * An organization this account can act in HAS BEEN RENAMED (#766).
   *
   * The scope list is a snapshot from `/auth/scopes`, taken at sign-in. Its
   * `name` is `tenants.name`, which the portal now projects over the club-info
   * blob — so after a club renames itself the snapshot is the stale copy, and
   * the projection puts the OLD name back over the one just saved. The header
   * then disagrees with the directory, and re-opening settings offers an
   * expectation the server will refuse.
   *
   * Takes the name the SERVER ACCEPTED, for the organization it accepted it
   * for. Ignores a tenant that is not in the list: a rename is only ever
   * acknowledged for an organization this account already acts in, and
   * borrowing a different one's name is the defect this whole issue is about.
   *
   * Cheaper than `refreshScopes()` and, more importantly, narrower: a refresh
   * would also re-decide the SELECTION, and a save is not a reason to move
   * which club somebody is working in.
   */
  scopeRenamed(tenantId: string, name: string): void;
  /** Ask again where this account may act. For retrying a failed discovery. */
  refreshScopes(): Promise<void>;
  /**
   * Re-establish a session from the cookie, on boot (#569).
   *
   * Asks the SERVER who this is — the browser holds no readable copy of
   * anything, by design, so there is nothing local to trust. Resolves true
   * when a session was restored.
   */
  restore(): Promise<boolean>;
  /**
   * ── THERE IS NO `signIn` ANY MORE (#754) ────────────────────────
   *
   * It posted an address and a password to `/auth/club/login`. The
   * credential is retired: no screen collects one, the route is gone, and a
   * method on this interface would be a way back in that nothing calls.
   *
   * A session is established by `adopt` (a door that issued one — the
   * e-mail code, a passkey, a dev persona) or by `restore` (the cookie).
   */
  /**
   * Take over a session the API ISSUED THROUGH ANOTHER DOOR — today the
   * dev-persona route (lib/api/devPersonas.ts). The token is real, minted by
   * the same issuer as login; what this skips is only the credential step
   * the other route already did. Discovers scopes exactly as sign-in does.
   */
  adopt(session: {
    token: string;
    expiresAt: string | number | null;
    account: ClubAccount;
  }): Promise<void>;
  /**
   * Take the bearer a completed STEP-UP issued (#750 M2b-1).
   *
   * NOT `adopt`, and the difference matters twice. `adopt` re-discovers scopes
   * and rebuilds the state from scratch, which would drop the operator's
   * CURRENT SCOPE selection — a step-up happens in the middle of
   * administrative work, and losing the club somebody is working in is a
   * worse interruption than the prompt itself. And it costs a round trip to
   * re-answer a question whose answer cannot have changed: the account is the
   * same account, and its memberships were not what the ceremony proved.
   *
   * So this swaps the credential and nothing else.
   */
  rotated(session: {
    token: string;
    expiresAt: string | number | null;
    /**
     * The `incarnation()` the ceremony STARTED under. Refused when it no
     * longer matches — see the implementation.
     */
    incarnation: number;
  }): boolean;
  /**
   * WHICH sign-in this is, as an opaque counter.
   *
   * Bumped by every transition that changes who is signed in or on what
   * credential: a sign-in, an adopt, a restore, and any drop. A caller that
   * captures it before a long operation can tell «still the session I started
   * under» from «a different one that happens to also be signed in».
   *
   * NOT THE TOKEN, deliberately. A cookie-restored session holds no token
   * (#620), so a null bearer is not an identity and comparing bearers would
   * make this guard silently absent for exactly the sessions that live
   * longest.
   */
  incarnation(): number;
  signOut(): Promise<void>;
  /**
   * Drop the session because the API refused it. Sends nothing.
   *
   * `forToken` is the bearer the refused request CARRIED. When it is given and
   * is not the one this session now holds, the refusal is IGNORED — see the
   * implementation for why that is the difference between a working step-up
   * and one that signs people out.
   */
  invalidate(forToken?: string): void;
  /**
   * The API refused `forToken` — is this session actually over? (#783)
   *
   * Asks the server once, COOKIE ONLY, and never ends anything itself: the
   * transport ends the session with `invalidate(forToken)` on `'final'`, so
   * `invalidate` stays the one local, request-free way a session is dropped.
   * See the implementation for each verdict.
   */
  checkRefusal(forToken: string): Promise<RefusalVerdict>;
  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: (state: ClubSessionState) => void): () => void;
}

/**
 * What `checkRefusal` found out (#783).
 *
 *   superseded  the cookie authenticates the SAME account, so a rotation this
 *               tab never heard about replaced the bearer. The bearer has been
 *               dropped; the session is kept, running on the cookie.
 *   final       no evidence the session is alive — the cookie was refused,
 *               belongs to somebody else, or the question failed. End it.
 *   moot        the session moved on while the question was asked (signed
 *               out, signed in again, or a fresh bearer installed here), so
 *               the refusal concerns nothing this session still holds.
 */
export type RefusalVerdict = 'superseded' | 'final' | 'moot';

/**
 * How long the cookie probe may take before it counts as «no evidence».
 *
 * Bounded because the transport WAITS for the verdict before handing the 401
 * to its caller, and a probe that never settled would hang that caller — a
 * write included, which the transport's own deadline deliberately does not
 * cover.
 */
export const REFUSAL_PROBE_TIMEOUT_MS = 10_000;

export interface ClubSessionDeps {
  baseUrl: () => string;
  fetchImpl: typeof fetch;
  now: () => number;
  /**
   * HOW THE TABS HEAR ABOUT EACH OTHER (#773). Injected so a test can drive
   * two real sessions through one real channel rather than fake the
   * coordination it is supposed to be testing.
   */
  channel: SessionChannel;
}

const SIGNED_OUT: ClubSessionState = {
  status: 'signed-out',
  account: null,
  scopes: [],
  scopeStatus: 'none',
  currentScope: null,
  endedBecause: null,
};

/** Parse the discovery response defensively — an older server may not have it. */
function readScopes(body: unknown): Scope[] {
  const raw = (body as { scopes?: unknown })?.scopes;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => r as Record<string, unknown>)
    .filter((r) => typeof r?.tenantId === 'string' && r.tenantId)
    .map((r) => ({
      tenantId: String(r.tenantId),
      slug: String(r.slug ?? ''),
      name: String(r.name ?? r.slug ?? ''),
      kind: String(r.kind ?? ''),
      roles: Array.isArray(r.roles) ? r.roles.map(String) : [],
    }));
}

export function createClubSession(deps: Partial<ClubSessionDeps> = {}): ClubSession {
  const baseUrl = deps.baseUrl ?? apiBaseUrl;
  const fetchImpl = deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const now = deps.now ?? (() => Date.now());
  // ── INERT UNLESS ONE IS SUPPLIED, DELIBERATELY ────────────────────
  //
  // The app-wide instance (lib/auth/instance.ts) passes the real
  // `BroadcastChannel`; everything else gets a channel that does nothing.
  //
  // This is not timidity about the default. A browser has ONE session instance
  // per tab, so cross-tab coordination and «one instance per process» are the
  // same thing there — but a test process holds dozens of sessions at once,
  // and with a real channel each one hears every other one's rotations. That
  // showed up exactly as you would fear: an unrelated step-up test failed in
  // the full run and passed alone, nondeterministically, because delivery is
  // asynchronous. An intermittently-wrong auth test is worse than a broken
  // one, so the coordination is something a caller asks for.
  const channel = deps.channel ?? createInertSessionChannel();

  let token: string | null = null;
  let expiresAt: number | null = null;
  /**
   * WHICH sign-in this is. Monotonic, bumped on every transition below.
   *
   * Exists because a step-up's completion can arrive after the session it
   * started under is gone — see `rotated`.
   */
  let incarnation = 0;
  let state: ClubSessionState = SIGNED_OUT;
  const listeners = new Set<(s: ClubSessionState) => void>();

  const set = (next: ClubSessionState) => {
    state = next;
    for (const l of [...listeners]) l(next);
  };

  const drop = (endedBecause: ClubSessionState['endedBecause']) => {
    token = null;
    expiresAt = null;
    incarnation += 1;
    // Everything goes, scope included: a new sign-in re-discovers where the
    // account may act rather than reusing a selection made under a session the
    // server has since refused.
    set({
      status: 'signed-out',
      account: null,
      scopes: [],
      scopeStatus: 'none',
      currentScope: null,
      endedBecause,
    });
  };

  /**
   * Ask the API where this account may act.
   *
   * A failure yields NO scopes rather than an error the caller must handle:
   * the person is signed in either way, and the portal then shows «nowhere to
   * act», which is the truth as far as it can tell. Guessing a scope would be
   * worse than showing none — it would put someone in an organization nobody
   * said they belonged to.
   */
  const loadScopes = async (
    base: string,
    // NULL AFTER A RELOAD (#569): the in-memory token is gone and the session
    // cookie is the credential. No header is sent then rather than an empty
    // one — `requireAuth` falls back to the cookie only when the header is
    // ABSENT, and `Bearer ` with nothing after it is a present, invalid header.
    bearer: string | null,
  ): Promise<{ answered: true; scopes: Scope[] } | { answered: false }> => {
    try {
      const res = await fetchImpl(base + '/auth/scopes', {
        credentials: 'include',
        ...(bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : {}),
      });
      // A 401 is handled by the transport elsewhere; here any non-2xx means the
      // question was not answered, which is NOT the same as «no scopes».
      if (!res.ok) return { answered: false };
      const body = await res.json().catch(() => null);
      if (body == null) return { answered: false };
      return { answered: true, scopes: readScopes(body) };
    } catch {
      return { answered: false };
    }
  };

  /** The state a discovery result implies. Shared by sign-in and retry. */
  const scopeStateFrom = (
    result: { answered: true; scopes: Scope[] } | { answered: false },
  ): Pick<ClubSessionState, 'scopes' | 'scopeStatus' | 'currentScope'> => {
    if (!result.answered) return { scopes: [], scopeStatus: 'undetermined', currentScope: null };
    const found = result.scopes;
    return {
      scopes: found,
      // One scope is not a choice — presenting it as one is a dialog whose
      // only button is the one you have to press.
      scopeStatus:
        found.length === 0 ? 'unavailable' : found.length === 1 ? 'selected' : 'choosing',
      currentScope: found.length === 1 ? found[0] : null,
    };
  };

  // ── A ROTATION SOMEWHERE ELSE IN THIS BROWSER (#773) ──────────────
  //
  // Drop the bearer and nothing else. The cookie is already the new
  // credential, so the next request authenticates against the rotated session
  // without this tab doing anything further — and because `incarnation` is
  // untouched, a step-up this tab started itself still completes.
  //
  // Never subscribed-and-forgotten: the listener lives as long as the session
  // instance, which lives as long as the tab.
  channel.subscribe((notice) => {
    if (state.status !== 'signed-in') return;
    // NOT OUR ACCOUNT, NOT OUR BUSINESS. The cookie is shared across tabs, so
    // a tab holding a bearer for A while the cookie belongs to B must keep
    // using A's bearer — dropping it would silently make this tab act as B.
    if (!state.account || state.account.id !== notice.accountId) return;
    if (token === null) return;
    token = null;
    expiresAt = null;
  });

  /**
   * Which account the browser's COOKIE authenticates as, or null (#783).
   *
   * `GET /me` with no Authorization header at all — the server consults the
   * cookie only when the header is absent, and a stale bearer would otherwise
   * be the thing answering. No tenant header either: this asks «who», never
   * «where», exactly as `restore()` does.
   *
   * Every failure is null: a refusal, a body without an account, a network
   * error and a timeout are all «no evidence the session is alive», and none
   * of them may keep a tab signed in.
   */
  const cookieAccount = async (base: string): Promise<string | null> => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = (async () => {
      const res = await fetchImpl(base + '/me', {
        credentials: 'include',
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!res.ok) return null;
      const body = (await res.json().catch(() => null)) as { account?: { id?: unknown } } | null;
      const id = body?.account?.id;
      return typeof id === 'string' && id !== '' ? id : null;
    })();
    // An abandoned probe still rejects when it is aborted; nobody is waiting
    // for it by then, and an unhandled rejection is noise at best.
    void ask.catch(() => null);
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        controller?.abort();
        resolve(null);
      }, REFUSAL_PROBE_TIMEOUT_MS);
    });
    try {
      return await Promise.race([ask, timeout]);
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  /**
   * The refusal check in progress, so a burst of 401s for one bearer asks
   * the server ONCE. A tab waking from a freeze typically fires several
   * requests at the same moment, and each would otherwise probe on its own.
   */
  let pendingCheck: { forToken: string; verdict: Promise<RefusalVerdict> } | null = null;

  return {
    state: () => state,

    tenantId: () => state.currentScope?.tenantId ?? null,

    async refreshScopes() {
      const base = baseUrl();
      // `this.token()` rather than the closure: an expired session must not be
      // retried with a token we already know is dead.
      const bearer = this.token();
      if (!base || !bearer) return;
      set({ ...state, ...scopeStateFrom(await loadScopes(base, bearer)) });
    },

    selectScope(tenantId) {
      const chosen = state.scopes.find((sc) => sc.tenantId === tenantId);
      // An id that is not on offer is ignored rather than trusted. The list
      // came from the server; a scope outside it was never granted, and the
      // server would refuse it anyway — but the portal should not ask.
      if (!chosen) return;
      // LEAVING ONE ORGANIZATION FOR ANOTHER closes the portal to writes until
      // the new scope's store has loaded (lib/api/scopeTransition.ts): the
      // tail of an operation begun under the old club must not land in the
      // new one. A first selection, or re-selecting the same scope, opens no
      // window.
      beginScopeTransition(state.currentScope?.tenantId ?? null, chosen.tenantId);
      set({ ...state, scopeStatus: 'selected', currentScope: chosen });
    },

    scopeRenamed(tenantId, name) {
      if (!tenantId || !name) return;
      const known = state.scopes.some((sc) => sc.tenantId === tenantId);
      if (!known) return;
      const scopes = state.scopes.map((sc) => (sc.tenantId === tenantId ? { ...sc, name } : sc));
      set({
        ...state,
        scopes,
        // THE SELECTION FOLLOWS ITS OWN SCOPE, by id. Re-finding it rather
        // than patching the held object keeps `currentScope` and the list the
        // same value, and leaves a selection pointing at a DIFFERENT
        // organization untouched.
        currentScope: state.currentScope
          ? (scopes.find((sc) => sc.tenantId === state.currentScope!.tenantId) ?? null)
          : null,
      });
    },

    token() {
      if (!token) return null;
      // An expiry the client can see is worth honouring: sending a token we
      // know is dead buys a guaranteed 401 and a round trip. The SERVER is
      // still the authority — this only avoids asking a question with a
      // known answer.
      if (expiresAt != null && now() >= expiresAt) {
        drop('unauthorized');
        return null;
      }
      return token;
    },

    // ── RESTORE FROM THE SERVER, NEVER FROM THE BROWSER (#569) ────
    //
    // The cookie is `HttpOnly`, so this code cannot read it and deliberately
    // does not try. It asks `/me` instead: if the browser still holds a live
    // session the request is authenticated by the cookie and answers with the
    // account, and if it does not the answer is 401 and this stays signed out.
    //
    // NO `endedBecause`. A failed restore is not «you were signed out» — it is
    // the ordinary state of somebody who has not signed in, and saying
    // otherwise would put a «your session ended» notice in front of every
    // first-time visitor.
    async restore() {
      const base = baseUrl();
      if (!base) return false;
      let res: Response;
      try {
        res = await fetchImpl(base + '/me', { credentials: 'include' });
      } catch {
        // NO ANSWER is not «no session» (#1145): the gate shows «no connection».
        noteRestoreUnreachable();
        return false;
      }
      if (!res.ok) {
        noteIfGateway(res.status);
        return false;
      }
      const body = (await res.json().catch(() => null)) as {
        account?: { id?: unknown; email?: unknown; displayName?: unknown };
      } | null;
      const id = body?.account?.id;
      if (typeof id !== 'string' || id === '') return false;

      // The token stays NULL: there is no readable copy of it anywhere, and the
      // cookie is what authenticates from here until the person signs in again.
      token = null;
      expiresAt = null;
      // A DIFFERENT SIGN-IN than whatever this tab held before — see
      // `incarnation()`. A step-up started before a reload must not land here.
      incarnation += 1;
      set({
        status: 'signed-in',
        account: {
          id,
          email: body?.account?.email == null ? null : String(body.account.email),
          displayName: body?.account?.displayName == null ? null : String(body.account.displayName),
        },
        ...scopeStateFrom(await loadScopes(base, null)),
        endedBecause: null,
      });
      return true;
    },

    async adopt(session) {
      const base = baseUrl();
      token = session.token;
      incarnation += 1;
      // The club login answers an ISO string; older shapes answered a number.
      // Parsed rather than assumed, so a NaN never becomes «never expires».
      const raw = session.expiresAt;
      const parsed =
        typeof raw === 'number' ? raw : typeof raw === 'string' ? Date.parse(raw) : NaN;
      expiresAt = Number.isFinite(parsed) ? parsed : null;
      set({
        status: 'signed-in',
        account: session.account,
        ...scopeStateFrom(base ? await loadScopes(base, session.token) : { answered: false }),
        endedBecause: null,
      });
    },

    async signOut() {
      const base = baseUrl();
      const current = token;
      // Local state is cleared FIRST and unconditionally. A sign-out that fails
      // on the network must still end signed out here — the alternative leaves
      // someone looking at a portal they asked to leave.
      drop('signed-out');
      // ATTEMPTED WHENEVER THERE IS AN API, not only when this tab still holds
      // a token (#569). After a reload the token is null and the cookie is the
      // only credential — and `if (!current) return` would then have skipped
      // the call entirely, leaving the database session live for its remaining
      // hours and the cookie in the browser. A logout that clears the screen
      // and nothing else is the worst of the three outcomes.
      if (!base) return;
      try {
        await fetchImpl(base + '/auth/club/logout', {
          method: 'POST',
          credentials: 'include',
          ...(current ? { headers: { Authorization: `Bearer ${current}` } } : {}),
        });
      } catch {
        /* the server's copy outliving ours is the server's to expire */
      }
    },

    incarnation() {
      return incarnation;
    },

    rotated(session) {
      // Only a LIVE session can be rotated. A late answer to a step-up started
      // just before a sign-out must not resurrect anything — the same reason
      // `reportImpersonationRefused` is a no-op when nothing is active.
      if (state.status !== 'signed-in') return false;
      // ── AND IT MUST BE THE SESSION THAT STARTED THE CEREMONY (#772) ──
      //
      // «Signed in» is not enough, and the gap is worse than the one it looks
      // like. A step-up begins under account A on T1; the server rotates to
      // T2 and the answer is delayed; A signs out; B signs in on T3; the late
      // T2 reply arrives. `status === 'signed-in'` is true — for B — so A's
      // bearer would be installed while B's account and scope stayed
      // selected. Every subsequent request would act as A under B's screen.
      //
      // The earlier test covered sign-out followed by a late rotation, which
      // the status check alone already handled, and could not see this
      // ordering at all.
      //
      // The counter is the identity, not the token: a cookie-restored session
      // holds no bearer, so comparing bearers would leave the sessions that
      // live longest unguarded.
      if (session.incarnation !== incarnation) return false;
      token = session.token;
      const raw = session.expiresAt;
      const parsed =
        typeof raw === 'number' ? raw : typeof raw === 'string' ? Date.parse(raw) : NaN;
      // THE DEADLINE ONLY EVER SHRINKS OR STAYS. The server inherits
      // `expiresAt` unchanged across a rotation, so a value further out than
      // what we hold would mean the two disagree about when this sign-in ends
      // — and trusting the larger one is the direction that keeps a session
      // alive locally after the server has stopped honouring it.
      if (Number.isFinite(parsed)) {
        expiresAt = expiresAt === null ? parsed : Math.min(expiresAt, parsed);
      }
      // The state object is unchanged except for having been re-emitted, so
      // subscribers re-render against a session that is still in the same
      // scope with the same account.
      //
      // `incarnation` is NOT bumped: this is the same sign-in with a fresh
      // credential, and bumping it would make a second step-up started before
      // this one completed refuse for no reason.
      set({ ...state });
      // ── AND THE OTHER TABS ARE TOLD (#773) ────────────────────────
      //
      // Posted AFTER the rotation has been accepted, so a refused one —
      // wrong incarnation, signed out — never announces anything. The
      // notice carries the account and this instance's id, never the
      // bearer: the cookie the step-up re-issued is what the other tabs
      // will authenticate with, and it is already in the browser.
      if (state.account) {
        channel.post({
          kind: 'club-credential-rotated',
          accountId: state.account.id,
          origin: channel.id,
        });
      }
      return true;
    },

    invalidate(forToken) {
      // ── A 401 FOR A SUPERSEDED BEARER IS NOT THIS SESSION'S (#750) ──
      //
      // A step-up REVOKES the previous token. Any request already in flight
      // when it completes therefore comes back 401 — and routed by prefix
      // alone, that 401 ended the session the step-up had just refreshed. The
      // feature defeated itself, and the symptom is the worst kind: signing
      // in again worked, so it reads as a flake.
      //
      // Demonstrated before this guard existed: adopt T1, rotate to T2, then
      // deliver a 401 for T1 — `status: 'signed-out'`.
      //
      // Compared against what we hold NOW, not against a list of past tokens:
      // the only question is whether the refusal concerns the credential this
      // session is currently using. Anything else is somebody else's problem,
      // already replaced.
      //
      // WITH NO TOKEN TO COMPARE, the refusal stands. A cookie-restored
      // session holds none, and `apiFetch` only reports a 401 it ATTACHED a
      // bearer to — so in practice this is reached with a token on both sides.
      // Erring toward ending the session is the safe direction for the case
      // that is left.
      //
      // The transport no longer reaches this on a bare 401 (#783): it asks
      // `checkRefusal` first and calls this only on `'final'`, once the
      // cookie has also failed to vouch for the same account. That includes
      // the one way a no-token session does meet a bearer refusal — a
      // request that left carrying the bearer a cross-tab notice then
      // dropped.
      if (forToken !== undefined && token !== null && forToken !== token) return;
      if (!token && state.status === 'signed-out') return;

      drop('unauthorized');
    },

    // ── A TAB THAT MISSED THE ROTATION NOTICE (#783) ──────────────────
    //
    // The cross-tab notice (#773) is best-effort: a frozen or suspended tab is
    // not running its handler, and older WebViews have no BroadcastChannel at
    // all. Such a tab still holds the bearer the rotation revoked, its next
    // request is refused, and `invalidate(forToken)` matches — because that
    // bearer genuinely IS this tab's credential. The tab signed itself out
    // while the browser held a perfectly good session for the same person.
    //
    // So before a refusal ends anything, the COOKIE is asked. A step-up
    // re-issues it, so after a rotation it is the live credential, and its
    // answer settles the question instead of inferring it:
    //
    //   the cookie is THIS account      superseded — drop the bearer, keep
    //                                   the session. Exactly the state a
    //                                   delivered notice would have produced.
    //   refused, ANOTHER account,       final — the transport ends the
    //   no answer, a timeout            session, as before.
    //
    // ── THE SAME-ACCOUNT CHECK IS THE LOAD-BEARING LINE ──────────────
    //
    // The cookie is shared by every tab, and the late Set-Cookie race (#773)
    // is a way for it to name somebody else. «The cookie authenticates» is
    // therefore not enough: keeping this tab signed in on another account's
    // cookie would render A's screen while every request acted as B. That is
    // a silent actor substitution, and it ends the session instead.
    //
    // ── WHAT THIS DOES NOT DO ────────────────────────────────────────
    //
    // It grants nothing. The server re-derives identity, capability, scope
    // and freshness on every request; this only decides whether a tab keeps
    // showing a session the server is still honouring for the same account.
    // It never adopts a credential (there is nothing to adopt — the cookie is
    // unreadable), never bumps `incarnation` (a step-up this tab started
    // still lands), and never ends the session itself: `'final'` is acted on
    // by the transport through `invalidate`, which stays local.
    checkRefusal(forToken) {
      // A refusal for a bearer already REPLACED here: `invalidate`'s own
      // guard, applied before asking anything, so the in-flight requests a
      // local step-up leaves behind cost no extra round trip.
      if (token !== null && forToken !== token) return Promise.resolve('moot');
      if (state.status !== 'signed-in' || !state.account) return Promise.resolve('moot');
      const base = baseUrl();
      if (!base) return Promise.resolve('final');
      if (pendingCheck && pendingCheck.forToken === forToken) return pendingCheck.verdict;

      const startedUnder = incarnation;
      const accountId = state.account.id;
      const verdict = (async (): Promise<RefusalVerdict> => {
        const answeredAs = await cookieAccount(base);
        // THE SESSION MOVED ON WHILE WE ASKED. A sign-out, a sign-in as
        // somebody else, or a restore bumps the incarnation; a step-up in
        // this tab installs a fresh bearer. Either way the refusal concerns
        // a credential this session no longer holds, and acting on the probe
        // would decide about a session it was not asked about.
        if (incarnation !== startedUnder || state.status !== 'signed-in') return 'moot';
        if (token !== null && token !== forToken) return 'moot';
        if (answeredAs === null || answeredAs !== accountId) return 'final';
        token = null;
        expiresAt = null;
        return 'superseded';
      })();
      pendingCheck = { forToken, verdict };
      void verdict.then(() => {
        if (pendingCheck?.verdict === verdict) pendingCheck = null;
      });
      return verdict;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
